import _refiner from 'refiner-js';
import Bowser from 'bowser';
import { DateTime } from 'luxon';
import { AppVersion } from 'app/version';
import { isE2ETest } from 'environments/environment-helpers';
import { AuthService, UserAuth } from './auth.service';
import { SettingsService } from './settings.service';
import { ConfigDbService } from './config-db.service';
import { ConfigKey } from './config-keys';
import { FitnessPlan } from '@cf-shared/data-model/fitness-plan';
import { UiLanguageTelemetryProps } from 'i18n/ui-language';
import {
  buildRefinerAttributionTraits, buildRefinerIntakeTraits,
  buildRefinerLocaleTraits, buildRefinerSubscriptionTraits,
} from './telemetry-profile-props';

const REFINER_PROJECT_ID = '1a18e1d0-8edb-11ee-9dd7-d3ba71c919cf';

/**
 * The parts of the session that Refiner needs and that this class must NOT hold a copy
 * of, because they change while the app runs: the acquisition attribution bag is
 * rewritten when a CouchDB login lands, and the identified user arrives after boot.
 *
 * `appDisplayMode` is in here for a different reason. TelemetryService owns the ONE
 * definition of it, because the Refiner device traits and the Mixpanel people profile
 * must report the same value — that agreement is the whole point of sending it — so
 * this class is handed the answer rather than working it out a second time.
 */
export interface RefinerSessionContext {
  trackingData: { [key: string]: any } | undefined;
  userId: string | undefined;
  appDisplayMode: 'native' | 'standalone' | 'browser';
  uiLanguage: UiLanguageTelemetryProps;
}

/*
Everything about the Refiner survey tool: loading its SDK, the enable/disable switch,
the trait vocabulary it segments on, and the config-driven list of events it is told
about. Refiner is a third-party vendor with its own SDK, its own failure modes and its
own names for things, none of which have anything to do with Mixpanel — TelemetryService
used to interleave the two at nine separate points.

A plain class constructed by TelemetryService, like TelemetryQueue and
TelemetryTransport. The auth service and the config database arrive later via initialize
because they do not exist when the service is constructed.

The pure trait builders stay in telemetry-profile-props.ts, where they are unit-tested;
this class composes what they return and hands it to the SDK.
*/
export class RefinerClient {
  private disabled = true;
  private authService: AuthService;
  private configDb: ConfigDbService;
  private eventsSet: Set<string> | null = null; // cached set of event names to send to Refiner

  constructor(
    private settings: SettingsService,
    private readContext: () => RefinerSessionContext,
  ) {
  }

  // Split from cacheEventsAsync, and both are called from TelemetryService.initialize at
  // the exact points the two used to sit at: the couchAuthChanged subject replays
  // synchronously on subscribe, so where that subscribe happens is not a free choice.
  initialize(authService: AuthService, configDb: ConfigDbService) {
    this.authService = authService;
    this.configDb = configDb;
    // top up Refiner when a deferred CouchDB login lands after the profile
    // identify already ran (#4325)
    this.watchCouchLogin();
  }

  // Called when the cookie banner grants analytics consent. Refiner stays off in the
  // automated end-to-end runs: it loads a script from a CDN and shows surveys, neither
  // of which belongs in a test run.
  enableForProject() {
    if (isE2ETest()) {
      console.log('Telemetry: E2E test mode detected, Refiner disabled');
      return;
    }
    this.disabled = false;
    console.log('Telemetry: Initializing Refiner project');
    this.call('setProject', REFINER_PROJECT_ID);
  }

  call(method: string, ...args: any[]) {
    if (this.disabled) {
      return;
    }
    try {
      const result: any = _refiner(method, ...args);
      // _refiner returns a promise when loading the CDN script — catch rejection from ad-blockers
      if (result && typeof result.catch === 'function') {
        result.catch((err: any) => console.warn('Telemetry: Refiner SDK failed to load:', err?.message || err));
      }
    } catch (err) {
      console.warn('Telemetry: Refiner call failed:', method, err);
    }
  }

  showTestSurvey() {
    this.call('showForm', 'TestSurvey', true);
  }

  trackScreen(screenName: string) {
    this.call('trackScreen', screenName);
    this.call('ping');
  }

  // Refiner only hears about the events named in the RefinerEvents config value.
  trackEventIfAllowed(eventName: string) {
    if (this.shouldSendEvent(eventName)) {
      this.call('trackEvent', eventName);
    }
  }

  /**
   * The login-time identify.
   *
   * Deliberately NO audience traits here (#4325) — this call runs too early to read
   * them. See identifyWithProfile for the full reason.
   */
  identifyAtLogin(user: UserAuth) {
    this.call('identifyUser', {
      id: user.uid,
      email: user.email,
      name: user.name,
      ...this.deviceTraits(),
      // acquisition attribution (the trackingData bag minus client_user_agent:
      // utm_*, gclid/fbclid, referrer) so surveys can be segmented by channel.
      // safe when absent — spreading an empty bag contributes nothing.
      ...this.attributionTraits(),
    });
  }

  /**
   * The full identify, sent from TelemetryService.sendUserProfileAsync.
   *
   * This runs for EVERY logged-in user, not only post-intake ones (#4325): it is the
   * earliest point where both the couch login result and the CouchDB-synced settings
   * are actually in memory, so it is the only place the audience traits can be sent
   * correctly. The login-time identify above runs BEFORE `telemetry.initialize` and
   * before the settings merge (both live inside app.component's `postLoginAsync`,
   * which is void-called one line earlier), so sending them there would report "no
   * subscription" and "never logged food" for everyone.
   *
   * The post-intake extras are spread in conditionally. Forwarding the SAME
   * engagement bag built for the Mixpanel profile — rather than hand-picking a few
   * fields — stops the two from drifting apart, so any user property added for
   * Mixpanel automatically reaches Refiner too. userProperties already carries
   * user_app_display_mode; deviceTraits sets the same value, and the later spread
   * just re-confirms it.
   */
  identifyWithProfile(
    user: UserAuth,
    intakeFinished: boolean,
    fitnessPlan: FitnessPlan,
    userProperties: { [key: string]: any },
  ) {
    this.call('identifyUser', {
      id: user.uid,
      email: user.email,
      name: user.name,

      ...this.deviceTraits(),

      // acquisition attribution (the trackingData bag minus client_user_agent:
      // utm_*, gclid/fbclid, referrer) for channel segmentation.
      ...this.attributionTraits(),

      // subscription status/provider, locale + country, and the food-log marker
      // (#4325). The UI-language traits in this bag are also inside
      // userProperties below, with identical values — the later spread just
      // re-confirms them, exactly like user_app_display_mode.
      ...this.audienceTraits(),

      ...buildRefinerIntakeTraits(intakeFinished, fitnessPlan, userProperties),
    });
  }

  /**
   * Re-identify so the "has logged food" survey segment updates in THIS session rather
   * than only at the next boot. The trait is re-sent on every later identify regardless,
   * so this is the only piece a failure here actually costs.
   */
  identifyFoodLogged(uid: string) {
    this.call('identifyUser', { id: uid, has_logged_food_ever: true });
  }

  /**
   * Re-send the audience traits when the CouchDB login lands AFTER the profile
   * identify already ran (#4325).
   *
   * On the warm-resume path `AuthService` emits the user with `couch = null` and
   * logs into CouchDB later, in Phase B. `sendUserProfileAsync` runs once per
   * boot, so on such a boot `buildRefinerSubscriptionTraits` correctly reports
   * nothing — but for a contact Refiner has never seen, "nothing" is not a stale
   * value it can fall back on, it is simply no subscription data at all until some
   * future boot happens to have the login ready. This closes that window.
   *
   * Subscribed once, in `initialize`. The subject is a BehaviorSubject, so it
   * replays its current value immediately on subscribe. On both real boot paths
   * the login has usually already happened by then, so that replay normally IS a
   * real login and just re-sends traits `sendUserProfileAsync` will send anyway —
   * harmless, and the cheapest way to also cover the case where it has not.
   * (`initialize` runs after app.component's CouchDB settings merge on both
   * paths, so the rest of the bag is already correct at replay time too.)
   */
  private watchCouchLogin(): void {
    this.authService.couchAuthChanged.subscribe((couch) => {
      // if the login has not landed, or no user is identified yet, there is
      // nothing to re-identify against
      const userId = this.readContext().userId;
      if (!couch || !userId) {
        return;
      }
      this.call('identifyUser', {
        id: userId,
        ...this.audienceTraits(),
      });
    });
  }

  // the device/app traits sent on every Refiner identifyUser call: coarse OS +
  // browser (from the user-agent), the app display mode (so Refiner can tell the
  // native app from mobile web, which user_os cannot), and the app version.
  private deviceTraits(): { user_os: string, user_browser: string, user_app_display_mode: 'native' | 'standalone' | 'browser', user_app_version: string } {
    const browserInfo = Bowser.getParser(window.navigator.userAgent).getResult();

    const knownOs = ['Windows', 'macOS', 'Linux', 'Android', 'iOS'];
    const knownBrowsers = ['Chrome', 'Edge', 'Safari', 'Opera', 'Samsung Internet', 'Firefox'];

    return {
      user_os: knownOs.find(os => os === browserInfo.os.name) || 'Other',
      user_browser: knownBrowsers.find(b => b === browserInfo.browser.name) || 'Other',
      user_app_display_mode: this.readContext().appDisplayMode,
      user_app_version: AppVersion,
    };
  }

  // the acquisition attribution we forward to Refiner. delegates to the pure
  // buildRefinerAttributionTraits (unit-tested in telemetry-profile-props.test.ts)
  // so the client_user_agent exclusion is locked by a test, not just a comment.
  private attributionTraits(): { [key: string]: any } {
    return buildRefinerAttributionTraits(this.readContext().trackingData);
  }

  /**
   * The audience traits Refiner segments on (#4325): subscription status and
   * provider, locale + country, and the sticky food-log marker.
   *
   * Sent ONLY from the full identify and the late-couch-login top-up, never from
   * the login-time one — see the comment there for why that call is too early to
   * read any of these correctly.
   *
   * Refiner ONLY. See buildRefinerSubscriptionTraits / buildRefinerLocaleTraits
   * for why the subscription and country traits must not also land on the Mixpanel
   * people profile.
   */
  private audienceTraits(): { [key: string]: any } {
    return {
      // `authService` is assigned in initialize, which every caller runs after, so
      // the optional chain is belt-and-braces rather than a real expectation. It
      // stays because a null couch is already a normal, handled input here.
      ...buildRefinerSubscriptionTraits(this.authService?.couch, DateTime.utc()),
      ...buildRefinerLocaleTraits(this.settings.locale, this.readContext().uiLanguage),
      has_logged_food_ever: this.settings.hasLoggedFoodEver,
    };
  }

  // Loads and caches the Refiner events config (comma-separated list of event names)
  async cacheEventsAsync(): Promise<void> {
    let configValue = await this.configDb.getConfigValueAsync<string>(ConfigKey.RefinerEvents);
    this.applyEvents(configValue);

    // reactively update when config changes via sync
    this.configDb.watchConfigValue<string>(ConfigKey.RefinerEvents, (newValue) => {
      this.applyEvents(newValue);
    });
  }

  private applyEvents(configValue: string | undefined): void {
    if (configValue) {
      // parse comma-separated list, trim whitespace from each event name
      this.eventsSet = new Set(
        configValue.split(',').map(e => e.trim()).filter(e => e.length > 0)
      );
      console.log(`Telemetry: Cached ${this.eventsSet.size} Refiner events: ${Array.from(this.eventsSet).join(', ')}`);
    } else {
      // if no config, don't send any events to Refiner
      this.eventsSet = null;
      console.log('Telemetry: No RefinerEvents config found, not sending events to Refiner');
    }
  }

  // Returns true if the event should be sent to Refiner
  private shouldSendEvent(eventName: string): boolean {
    // if no config set, don't send any events
    if (this.eventsSet === null) {
      return false;
    }
    return this.eventsSet.has(eventName);
  }
}
